<template>
  <div class="app-surface" @click="requestMobileBrowserChromeCollapse">
    <NavBar />

    <main class="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8">
      <section
        class="relative mb-8 cursor-grab overflow-hidden rounded-2xl border border-white/20 bg-ink-950 shadow-fruit active:cursor-grabbing"
        @touchstart.passive="startHeroDrag"
        @touchmove.passive="moveHeroDrag"
        @touchend.passive="endHeroDrag"
        @mousedown="startHeroDrag"
        @mousemove="moveHeroDrag"
        @mouseup="endHeroDrag"
        @mouseleave="endHeroDrag"
      >
        <div
          class="flex transition-transform duration-500 ease-out"
          :class="{ 'duration-0': isHeroDragging }"
          :style="{ transform: `translateX(calc(-${activeHeroSlide * 100}% + ${heroDragOffset}px))` }"
        >
          <div class="relative isolate w-full shrink-0 overflow-hidden bg-[#120f17]">
            <img
              :src="homeHeroBg"
              alt=""
              class="pointer-events-none absolute inset-y-0 right-0 -z-20 h-full w-auto max-w-none select-none opacity-95 [mask-image:linear-gradient(to_right,transparent_0%,black_26%,black_100%)]"
            />
            <div class="absolute inset-0 -z-10 bg-gradient-to-r from-ink-950 from-[0%] via-ink-950/78 via-[38%] to-ink-950/0 to-[74%]"></div>
            <div class="absolute inset-0 -z-10 bg-gradient-to-t from-ink-950/52 via-transparent to-white/8"></div>
            <div class="min-h-[20rem] p-5 md:min-h-[23rem] md:p-8">
              <div class="flex min-h-[17.5rem] flex-col justify-between gap-6 md:min-h-[19rem]">
                <div>
                  <div class="mb-3 inline-flex items-center gap-2 rounded-lg bg-white/16 px-3 py-1.5 text-xs font-black text-white shadow-sm ring-1 ring-white/20 backdrop-blur-md">
                    今日开刷
                    <span class="h-1 w-1 rounded-full bg-pulp-500"></span>
                    AI 网文乐园
                  </div>
                  <h1 class="max-w-2xl text-3xl font-black leading-tight text-white [text-shadow:0_3px_20px_rgba(0,0,0,0.82)] md:text-5xl">
                    今天想看点甜的，还是爽的？
                  </h1>
                  <p class="mt-4 max-w-xl text-sm font-semibold leading-7 text-white [text-shadow:0_2px_14px_rgba(0,0,0,0.86)] md:text-base">
                    菠萝蜜把热门书单、AI 续写和剧情分支放到同一个阅读入口。打开就能追，看到章末还能换个走向。
                  </p>
                </div>

                <div class="flex flex-wrap gap-3">
                  <router-link to="/books" class="rounded-xl bg-jackfruit-500 px-5 py-3 text-sm font-black text-ink-900 shadow-fruit transition hover:bg-jackfruit-600">
                    开始找书
                  </router-link>
                  <router-link to="/create" class="rounded-xl bg-white px-5 py-3 text-sm font-black text-ink-900 shadow-soft transition hover:bg-jackfruit-50">
                    自由创作
                  </router-link>
                  <router-link to="/books?category=male" class="rounded-xl border border-white/24 bg-white/12 px-5 py-3 text-sm font-bold text-white shadow-sm backdrop-blur-md transition hover:border-jackfruit-300 hover:bg-white/20">
                    男频爽文
                  </router-link>
                  <router-link to="/books?category=female" class="rounded-xl border border-white/24 bg-white/12 px-5 py-3 text-sm font-bold text-white shadow-sm backdrop-blur-md transition hover:border-berry-300 hover:bg-white/20">
                    女频甜宠
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <div class="relative isolate w-full shrink-0 overflow-hidden bg-[#10151f]">
            <img
              :src="homeHeroCreateBg"
              alt=""
              class="pointer-events-none absolute inset-y-0 right-0 -z-20 h-full w-auto max-w-none select-none opacity-95 [mask-image:linear-gradient(to_right,transparent_0%,black_26%,black_100%)]"
            />
            <div class="absolute inset-0 -z-10 bg-gradient-to-r from-ink-950 from-[0%] via-ink-950/76 via-[38%] to-ink-950/0 to-[74%]"></div>
            <div class="absolute inset-0 -z-10 bg-gradient-to-t from-ink-950/52 via-transparent to-white/8"></div>
            <div class="min-h-[20rem] p-5 md:min-h-[23rem] md:p-8">
              <div class="flex min-h-[17.5rem] flex-col justify-between gap-6 md:min-h-[19rem]">
                <div>
                  <div class="mb-3 inline-flex items-center gap-2 rounded-lg bg-white/16 px-3 py-1.5 text-xs font-black text-white shadow-sm ring-1 ring-white/20 backdrop-blur-md">
                    创作者计划
                    <span class="h-1 w-1 rounded-full bg-jackfruit-500"></span>
                    自由创作
                  </div>
                  <h2 class="max-w-2xl text-3xl font-black leading-tight text-white [text-shadow:0_3px_20px_rgba(0,0,0,0.82)] md:text-5xl">
                    你的一个开头，也能长成一本书
                  </h2>
                  <p class="mt-4 max-w-xl text-sm font-semibold leading-7 text-white [text-shadow:0_2px_14px_rgba(0,0,0,0.86)] md:text-base">
                    写下 100 字以上的故事开端，AI 帮你生成书名、封面和后续章节。满 5 章可申请上架，未来还能参与创作者收益分成。
                  </p>
                </div>

                <div class="flex flex-wrap gap-3">
                  <router-link to="/create" class="rounded-xl bg-white px-5 py-3 text-sm font-black text-ink-900 shadow-soft transition hover:bg-jackfruit-50">
                    现在开始写
                  </router-link>
                  <router-link to="/profile" class="rounded-xl border border-white/24 bg-white/12 px-5 py-3 text-sm font-bold text-white shadow-sm backdrop-blur-md transition hover:bg-white/20">
                    我的创作
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center gap-2">
          <button
            v-for="index in 2"
            :key="index"
            class="pointer-events-auto h-2 rounded-full transition-all"
            :class="activeHeroSlide === index - 1 ? 'w-7 bg-jackfruit-400' : 'w-2 bg-white/45'"
            @click="goToHeroSlide(index - 1)"
          ></button>
        </div>
      </section>

      <section class="mb-7 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
        <router-link
          v-for="channel in quickChannels"
          :key="channel.to"
          :to="channel.to"
          class="group relative isolate flex aspect-square min-w-0 items-end overflow-hidden rounded-2xl border border-white/20 bg-ink-900 p-4 shadow-soft transition duration-300 hover:-translate-y-0.5 hover:shadow-fruit md:p-5"
        >
          <img
            :src="channel.image"
            alt=""
            class="pointer-events-none absolute inset-0 -z-20 h-full w-full object-cover transition duration-500 group-hover:scale-105"
          />
          <div class="absolute inset-0 -z-10 bg-gradient-to-t from-ink-950/86 via-ink-950/34 to-transparent"></div>
          <div class="absolute inset-0 -z-10 bg-gradient-to-r from-ink-950/42 via-transparent to-transparent"></div>
          <span class="text-2xl font-black leading-none text-white [text-shadow:0_3px_16px_rgba(0,0,0,0.86)] sm:text-3xl md:text-2xl lg:text-3xl">
            {{ channel.title }}
          </span>
        </router-link>
      </section>

      <section v-if="featuredBook" class="mb-8 rounded-2xl border border-jackfruit-100 bg-white p-4 shadow-soft">
        <div class="mb-3 flex items-center justify-between">
          <div>
            <p class="text-xs font-black text-pulp-600">HOT SHELF</p>
            <h2 class="mt-1 text-lg font-black text-ink-900">今日上头榜</h2>
          </div>
          <router-link to="/books?sort=views" class="text-xs font-bold text-ink-500 hover:text-pulp-600">更多</router-link>
        </div>
        <div class="grid grid-cols-[5.5rem_minmax(0,1fr)] gap-3 rounded-xl bg-jackfruit-50/70 p-3 sm:grid-cols-[7rem_minmax(0,1fr)]">
          <router-link :to="`/books/${featuredBook.id}`" class="block overflow-hidden rounded-lg">
            <img
              v-if="getCoverUrl(featuredBook)"
              :src="getCoverUrl(featuredBook)"
              :alt="featuredBook.title"
              class="aspect-[3/4] w-full object-cover"
              @error="(e) => ((e.target as HTMLImageElement).style.display = 'none')"
            />
            <div v-else class="grid aspect-[3/4] place-items-center bg-jackfruit-100 text-3xl">📖</div>
          </router-link>
          <div class="min-w-0 py-0.5">
            <div class="mb-1.5 inline-flex rounded-md bg-berry-50 px-2 py-1 text-[10px] font-black text-berry-600">
              读者追疯了
            </div>
            <h3 class="line-clamp-2 text-base font-black text-ink-900 sm:text-lg">{{ featuredBook.title }}</h3>
            <p class="mt-1.5 line-clamp-2 text-xs leading-5 text-ink-500 sm:line-clamp-3">{{ featuredBook.description }}</p>
            <div class="mt-2 flex items-center gap-2 text-xs font-bold text-ink-500">
              <span>🔥 {{ formatViewCount(featuredBook.viewCount) }}追读</span>
              <span>{{ featuredBook.totalChapters }}章</span>
            </div>
          </div>
        </div>
      </section>

      <div v-if="isLoading" class="flex justify-center py-16">
        <div class="h-11 w-11 animate-spin rounded-full border-b-2 border-jackfruit-500"></div>
        <span class="ml-3 text-ink-500">加载中...</span>
      </div>

      <div v-else class="space-y-10">
        <section v-if="hotBooks.length">
          <div class="mb-4 flex items-end justify-between">
            <div>
              <p class="text-xs font-black text-pulp-600">TRENDING</p>
              <h2 class="mt-1 text-xl font-black text-ink-900">今日上头</h2>
            </div>
            <router-link to="/books?sort=views" class="text-sm font-bold text-ink-500 hover:text-pulp-600">全部</router-link>
          </div>
          <div class="flex gap-4 overflow-x-auto pb-3 scrollbar-hide">
            <div v-for="book in hotBooks" :key="book.id" class="w-[31vw] min-w-[6.5rem] max-w-[8rem] shrink-0 sm:w-[196px] sm:max-w-none">
              <BookCard :book="book" />
            </div>
          </div>
        </section>

        <section
          v-for="section in categorySections"
          :key="section.key"
          v-show="section.books.length"
        >
          <div class="mb-4 flex items-end justify-between">
            <div>
              <p class="text-xs font-black" :class="section.kickerClass">{{ section.kicker }}</p>
              <h2 class="mt-1 text-xl font-black text-ink-900">{{ section.title }}</h2>
            </div>
            <router-link :to="section.to" class="text-sm font-bold text-ink-500 hover:text-pulp-600">全部</router-link>
          </div>
          <div class="grid grid-cols-3 gap-2.5 sm:grid-cols-3 sm:gap-4 lg:grid-cols-4 xl:grid-cols-6">
            <BookCard v-for="book in section.books.slice(0, 6)" :key="book.id" :book="book" />
          </div>
        </section>

        <div v-if="!allBooks.length" class="rounded-2xl border border-ink-100 bg-white p-12 text-center shadow-soft">
          <div class="text-5xl">📖</div>
          <p class="mt-4 text-lg font-black text-ink-900">暂无书籍</p>
          <p class="mt-2 text-sm text-ink-500">请先执行 seed 导入数据。</p>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { bookApi } from '@/api'
import NavBar from '@/components/NavBar.vue'
import BookCard from '@/components/BookCard.vue'
import type { Book } from '@/types/user'
import { requestMobileBrowserChromeCollapse } from '@/utils/browserChrome'
import homeHeroBg from '@/assets/images/home-hero-bg.jpg'
import homeHeroCreateBg from '@/assets/images/home-hero-create-bg.jpg'
import homeChannelCyberpunk from '@/assets/images/home-channel-cyberpunk.jpg'
import homeChannelMale from '@/assets/images/home-channel-male.jpg'
import homeChannelFemale from '@/assets/images/home-channel-female.jpg'
import homeChannelCreate from '@/assets/images/home-channel-create.jpg'

const allBooks = ref<Book[]>([])
const isLoading = ref(true)
const activeHeroSlide = ref(0)
const isHeroDragging = ref(false)
const heroDragStartX = ref(0)
const heroDragOffset = ref(0)
let heroTimer: number | null = null

const maleBooks = computed(() => allBooks.value.filter(b => b.category === 'male'))
const femaleBooks = computed(() => allBooks.value.filter(b => b.category === 'female'))
const generalBooks = computed(() => allBooks.value.filter(b => b.category === 'general'))
const cyberpunkBooks = computed(() => allBooks.value.filter(b => b.category === 'cyberpunk'))
const hotBooks = computed(() =>
  [...allBooks.value].sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0)).slice(0, 8)
)
const featuredBook = computed(() => hotBooks.value[0])

const quickChannels = [
  { title: '赛博', to: '/books?category=cyberpunk', image: homeChannelCyberpunk },
  { title: '男频', to: '/books?category=male', image: homeChannelMale },
  { title: '女频', to: '/books?category=female', image: homeChannelFemale },
  { title: '创作', to: '/create', image: homeChannelCreate },
]

const categorySections = computed(() => [
  { key: 'cyberpunk', kicker: 'AI FEVER', title: '赛博朋克新入口', to: '/books?category=cyberpunk', books: cyberpunkBooks.value, kickerClass: 'text-cyan-600' },
  { key: 'male', kicker: 'POWER UP', title: '男频爽感拉满', to: '/books?category=male', books: maleBooks.value, kickerClass: 'text-male-600' },
  { key: 'female', kicker: 'SWEET HIT', title: '女频甜到上头', to: '/books?category=female', books: femaleBooks.value, kickerClass: 'text-berry-600' },
  { key: 'general', kicker: 'NIGHT READ', title: '今晚适合悬疑与幻想', to: '/books?category=general', books: generalBooks.value, kickerClass: 'text-leaf-600' },
])

const homeCategories: Array<'cyberpunk' | 'male' | 'female' | 'general'> = ['cyberpunk', 'male', 'female', 'general']

const getCoverUrl = (book: Book) => {
  if (!book.coverUrl) return ''
  if (book.coverUrl.startsWith('http')) return book.coverUrl
  return book.coverUrl
}

const formatViewCount = (count: number) => {
  if (!count) return '0'
  if (count >= 10000) return (count / 10000).toFixed(1) + '万'
  return count.toString()
}

const resetHeroTimer = () => {
  if (heroTimer !== null) {
    window.clearInterval(heroTimer)
  }
  heroTimer = window.setInterval(() => {
    activeHeroSlide.value = (activeHeroSlide.value + 1) % 2
  }, 5500)
}

const getClientX = (event: TouchEvent | MouseEvent) => {
  return 'touches' in event ? event.touches[0]?.clientX || 0 : event.clientX
}

const goToHeroSlide = (index: number) => {
  activeHeroSlide.value = index
  heroDragOffset.value = 0
  resetHeroTimer()
}

const startHeroDrag = (event: TouchEvent | MouseEvent) => {
  isHeroDragging.value = true
  heroDragStartX.value = getClientX(event)
  heroDragOffset.value = 0
}

const moveHeroDrag = (event: TouchEvent | MouseEvent) => {
  if (!isHeroDragging.value) return
  const delta = getClientX(event) - heroDragStartX.value
  if ((activeHeroSlide.value === 0 && delta > 0) || (activeHeroSlide.value === 1 && delta < 0)) {
    heroDragOffset.value = delta * 0.28
    return
  }
  heroDragOffset.value = delta
}

const endHeroDrag = () => {
  if (!isHeroDragging.value) return
  const threshold = 48
  if (heroDragOffset.value < -threshold && activeHeroSlide.value < 1) {
    activeHeroSlide.value += 1
  } else if (heroDragOffset.value > threshold && activeHeroSlide.value > 0) {
    activeHeroSlide.value -= 1
  }
  isHeroDragging.value = false
  heroDragOffset.value = 0
  resetHeroTimer()
}

onMounted(async () => {
  resetHeroTimer()

  try {
    const responses = await Promise.all(
      homeCategories.map(category =>
        bookApi.getBooks({ category, limit: 12, status: 'published' })
      )
    )

    allBooks.value = responses
      .flatMap(res => (res.code === 200 && res.data?.items ? res.data.items : []))
      .map(normalizeBook)
  } catch (err) {
    console.error('加载书籍失败:', err)
  } finally {
    isLoading.value = false
  }
})

onBeforeUnmount(() => {
  if (heroTimer !== null) {
    window.clearInterval(heroTimer)
  }
})

const normalizeBook = (book: Book): Book => ({
  ...book,
  tags: parseTags(book.tags),
  coverUrl: book.coverUrl || '',
  description: book.description || '',
})

const parseTags = (tags: any): string[] => {
  if (Array.isArray(tags)) return tags
  if (typeof tags !== 'string') return []
  try {
    const parsed = JSON.parse(tags)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return tags.split(',').map((tag: string) => tag.trim()).filter(Boolean)
  }
}
</script>
